import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { servicePredictorApiService } from '../../services/servicePredictorApiService';
import type {
  CheckAPIKeyHealthRequest,
  CheckAPIKeyHealthResponse,
} from '../types';

// Load credentials from localStorage on initialization
const loadStoredCredentials = () => {
  if (typeof window === 'undefined') {
    return { operatorKey: '', email: '' };
  }
  return {
    operatorKey: localStorage.getItem('operator_key') || '',
    email: localStorage.getItem('email') || '',
  };
};

// Async thunk for checking API key health
export const checkAPIKeyHealth = createAsyncThunk<
  CheckAPIKeyHealthResponse,
  CheckAPIKeyHealthRequest,
  { rejectValue: { error?: string; canceled?: boolean } }
>('auth/checkAPIKeyHealth', async ({ params, signal }, { rejectWithValue }) => {
  try {
    const result = await servicePredictorApiService.checkAPIKeyHealth({
      ...params,
      ...(signal && { signal }),
    });

    if (result.canceled) {
      return rejectWithValue({ canceled: true });
    }

    if (result.error) {
      return rejectWithValue({ error: result.error });
    }

    // Save credentials to localStorage on success
    if (result.isValid && typeof window !== 'undefined') {
      localStorage.setItem('operator_key', params.operatorKey);
      localStorage.setItem('email', params.email);
      // Initialize the API service with the operator key
      servicePredictorApiService.initialize(params.operatorKey);
    }

    return result;
  } catch (error: any) {
    // Remove credentials on error
    if (typeof window !== 'undefined') {
      localStorage.removeItem('operator_key');
      localStorage.removeItem('email');
    }
    // Clear the operator key from the API service
    servicePredictorApiService.clearOperatorKey();
    return rejectWithValue({
      error: error.message || 'Failed to check API key',
    });
  }
});

interface AuthState {
  operatorKey: string;
  email: string;
  isValid: boolean;
  operator: string | null;
  isTarana: boolean;
  loading: boolean;
  error: string | null;
}

const storedCredentials = loadStoredCredentials();

// Note: We load credentials to pre-fill the form, but do NOT automatically
// initialize the API service or authenticate. User must manually authenticate.

const initialState: AuthState = {
  operatorKey: storedCredentials.operatorKey,
  email: storedCredentials.email,
  isValid: false,
  operator: null,
  isTarana: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    clearCredentials: (state) => {
      state.operatorKey = '';
      state.email = '';
      state.isValid = false;
      state.operator = null;
      state.isTarana = false;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('operator_key');
        localStorage.removeItem('email');
      }
      // Clear the operator key from the API service
      servicePredictorApiService.clearOperatorKey();
    },
    setCredentials: (
      state,
      action: PayloadAction<{ operatorKey: string; email: string }>
    ) => {
      state.operatorKey = action.payload.operatorKey;
      state.email = action.payload.email;
      // Initialize the API service with the operator key
      if (action.payload.operatorKey) {
        servicePredictorApiService.initialize(action.payload.operatorKey);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAPIKeyHealth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        checkAPIKeyHealth.fulfilled,
        (state, action: PayloadAction<CheckAPIKeyHealthResponse>) => {
          state.loading = false;
          state.isValid = action.payload.isValid;
          state.operator = action.payload.operator || null;
          state.isTarana = action.payload.isTarana || false;
          state.error = null;
        }
      )
      .addCase(checkAPIKeyHealth.rejected, (state, action) => {
        state.loading = false;
        state.isValid = false;
        state.operator = null;
        state.isTarana = false;
        if (!action.payload?.canceled) {
          state.error =
            (action.payload as { error?: string })?.error ||
            'Failed to check API key';
          // Remove credentials on error
          if (typeof window !== 'undefined') {
            localStorage.removeItem('operator_key');
            localStorage.removeItem('email');
          }
          // Clear the operator key from the API service
          servicePredictorApiService.clearOperatorKey();
        }
      });
  },
});

export const { clearAuthError, clearCredentials, setCredentials } =
  authSlice.actions;
export default authSlice.reducer;
